// The panel layer. The ONLY file that touches the Samsung b2bapis / webapis / tizen globals (plan 4.3).
// Everything else takes a `SignagePanel` and is tested with a mock.
//
// Every call name below is "to verify" (plan 4.2 item 2). S0 fixes names and firmware minimums HERE and
// records them in docs/samsung-provisioning.md. A call that isn't present makes its capability unavailable.

export interface PanelAvailability {
    reboot: boolean;
    captureScreen: boolean;
    readFile: boolean;
    panelMute: boolean;
    serial: boolean;
    model: boolean;
}

/** The signage display the Samsung host controls. */
export interface SignagePanel {
    /** True when the panel's APIs exist at all (we run on a Tizen / SSSP engine). */
    isPresent(): boolean;
    /** Loads Samsung's `webapis` / `b2bapis` scripts; they are not globals until the page includes them. Never rejects. */
    loadApis(): Promise<void>;
    availability(): PanelAvailability;
    rebootDevice(): Promise<void>;
    /** Returns the path of the captured image file. */
    captureScreen(): Promise<string>;
    readFile(path: string): Promise<Uint8Array>;
    deleteFile(path: string): Promise<void>;
    getSerialNumber(): string | null;
    getMacAddress(): string | null;
    getModel(): string | null;
    getFirmware(): string | null;
    /** Tizen platform version ("6.5"), from `tizen.systeminfo`. */
    getPlatformVersion(): string | null;
    /** The panel's own LAN address, from `webapis.network`. */
    getIpAddress(): string | null;
    /** Version of the installed widget (`config.xml`), as Tizen reports it. */
    getAppVersion(): string | null;
    /** Panel mute = backlight + picture off while the app keeps running (D6). */
    setPanelMute(on: boolean): Promise<void>;
    getPanelMute(): boolean | null;
    /** Asks the engine to deliver a remote key (Tizen key name) to the page. Returns its keyCode, or null when it can't. */
    registerRemoteKey(name: string): number | null;
}

/** A panel call the firmware doesn't expose. Its capability reports `false`. */
export class PanelCallUnavailable extends Error {
    constructor(call: string) {
        super(`call unavailable on this panel: ${call}`);
    }
}

// Shapes of the Tizen globals. Names are Samsung's and can't be renamed.
type Callback<T> = (value: T) => void;
type ErrorCallback = (err: { name?: string; message?: string }) => void;
type AsyncCall<A extends unknown[], T> = (...args: [...A, Callback<T>, ErrorCallback]) => void;

/** Shape of Samsung's `b2bapis.b2bcontrol` object. */
interface TizenControlApi {
    rebootDevice?: AsyncCall<[], void>;
    captureScreen?: AsyncCall<[], string>;
    getSerialNumber?: () => string;
    getMACAddress?: () => string;
    setPanelMute?: AsyncCall<["ON" | "OFF"], void>;
    getPanelMuteStatus?: () => "ON" | "OFF";
}

/** Shape of Samsung's `webapis.network` object. */
interface TizenNetworkApi {
    getIp?: () => string;
}

/** Shape of Samsung's `webapis.productinfo` object. */
interface TizenProductInfoApi {
    getRealModel?: () => string;
    getModel?: () => string;
    getFirmware?: () => string;
}

interface TizenFile {
    readData(): Uint8Array;
    close(): void;
}

/** Shape of Samsung's `tizen.filesystem` object. */
interface TizenFilesystemApi {
    openFile?: (path: string, mode: "r" | "w" | "a" | "rw") => TizenFile;
    deleteFile?: (path: string, ok?: () => void, err?: ErrorCallback) => void;
}

/** The globals the panel's browser engine exposes. */
export interface TizenGlobals {
    document?: Pick<Document, "createElement" | "head">;
    b2bapis?: { b2bcontrol?: TizenControlApi };
    webapis?: {
        productinfo?: TizenProductInfoApi;
        network?: TizenNetworkApi;
        avplay?: unknown;
        tvinfo?: unknown;
    };
    tizen?: {
        filesystem?: TizenFilesystemApi;
        systeminfo?: {
            getCapability?: (key: string) => unknown;
        };
        application?: {
            getCurrentApplication?: () => { exit(): void };
            getAppInfo?: () => { version?: string };
        };
        tvinputdevice?: {
            registerKey?: (key: string) => void;
            getKey?: (key: string) => { name: string; code: number } | null;
        };
    };
}

function promisify<A extends unknown[], T>(name: string, fn: AsyncCall<A, T> | undefined, ...args: A): Promise<T> {
    if (!fn) return Promise.reject(new PanelCallUnavailable(name));
    return new Promise<T>((resolve, reject) => {
        try {
            fn(...args, resolve, (err) => reject(new Error(`${name}: ${err?.name ?? ""} ${err?.message ?? ""}`.trim())));
        } catch (e) {
            reject(e);
        }
    });
}

function safe<T>(fn: (() => T) | undefined): T | null {
    if (!fn) return null;
    try {
        return fn() ?? null;
    } catch {
        return null;
    }
}

/** `SignagePanel` backed by the real Tizen / SSSP globals. */
export class TizenSignagePanel implements SignagePanel {
    constructor(private readonly globals: TizenGlobals = globalThis as unknown as TizenGlobals) {}

    // Read on every call, never cached.
    private get control(): TizenControlApi | undefined {
        return this.globals.b2bapis?.b2bcontrol;
    }

    private get productInfo(): TizenProductInfoApi | undefined {
        return this.globals.webapis?.productinfo;
    }

    private get filesystem(): TizenFilesystemApi | undefined {
        return this.globals.tizen?.filesystem;
    }

    isPresent(): boolean {
        return this.globals.tizen !== undefined || this.globals.b2bapis !== undefined;
    }

    async loadApis(): Promise<void> {
        // `$WEBAPIS` / `$B2BAPIS` are resolved by the panel's engine; elsewhere these URLs mean nothing.
        if (!this.globals.webapis) await this.loadScript("$WEBAPIS/webapis/webapis.js");
        if (!this.globals.b2bapis) await this.loadScript("$B2BAPIS/b2bapis/b2bapis.js");
    }

    private loadScript(src: string): Promise<void> {
        const doc = this.globals.document;
        if (!doc?.head) return Promise.resolve();
        return new Promise((resolve) => {
            const script = doc.createElement("script");
            script.src = src;
            script.onload = () => resolve();
            script.onerror = () => {
                console.warn(`[SignagePanel] could not load ${src}`);
                resolve();
            };
            doc.head.appendChild(script);
        });
    }

    availability(): PanelAvailability {
        const ctl = this.control;
        const product = this.productInfo;
        return {
            reboot: typeof ctl?.rebootDevice === "function",
            captureScreen: typeof ctl?.captureScreen === "function",
            readFile: typeof this.filesystem?.openFile === "function",
            panelMute: typeof ctl?.setPanelMute === "function",
            serial: typeof ctl?.getSerialNumber === "function",
            model: typeof product?.getRealModel === "function" || typeof product?.getModel === "function",
        };
    }

    rebootDevice(): Promise<void> {
        const ctl = this.control;
        return promisify("rebootDevice", ctl?.rebootDevice?.bind(ctl));
    }

    captureScreen(): Promise<string> {
        const ctl = this.control;
        return promisify("captureScreen", ctl?.captureScreen?.bind(ctl));
    }

    async readFile(path: string): Promise<Uint8Array> {
        const fs = this.filesystem;
        if (!fs?.openFile) throw new PanelCallUnavailable("tizen.filesystem.openFile");
        // captureScreen may hand back a plain path or a file:// URI; openFile takes either on some firmware only.
        const candidates = path.startsWith("file://") ? [path, path.slice(7)] : [path, `file://${path}`];
        let lastError: unknown;
        for (const candidate of candidates) {
            let file: TizenFile;
            try {
                file = fs.openFile(candidate, "r");
            } catch (e) {
                lastError = e;
                continue;
            }
            try {
                return file.readData();
            } finally {
                file.close();
            }
        }
        throw lastError;
    }

    deleteFile(path: string): Promise<void> {
        const fs = this.filesystem;
        if (!fs?.deleteFile) return Promise.resolve();
        return new Promise((resolve) => fs.deleteFile!(path, () => resolve(), () => resolve()));
    }

    getSerialNumber(): string | null {
        const ctl = this.control;
        return safe(ctl?.getSerialNumber?.bind(ctl));
    }

    getMacAddress(): string | null {
        const ctl = this.control;
        return safe(ctl?.getMACAddress?.bind(ctl));
    }

    getModel(): string | null {
        const product = this.productInfo;
        return safe(product?.getRealModel?.bind(product)) ?? safe(product?.getModel?.bind(product));
    }

    getFirmware(): string | null {
        const product = this.productInfo;
        return safe(product?.getFirmware?.bind(product));
    }

    getPlatformVersion(): string | null {
        const info = this.globals.tizen?.systeminfo;
        return safe(() => {
            const v = info?.getCapability?.("http://tizen.org/feature/platform.version");
            return typeof v === "string" && v !== "" ? v : null;
        });
    }

    getIpAddress(): string | null {
        const network = this.globals.webapis?.network;
        return safe(() => network?.getIp?.() || null);
    }

    getAppVersion(): string | null {
        const app = this.globals.tizen?.application;
        return safe(() => app?.getAppInfo?.()?.version || null);
    }

    setPanelMute(on: boolean): Promise<void> {
        const ctl = this.control;
        return promisify("setPanelMute", ctl?.setPanelMute?.bind(ctl), on ? "ON" : "OFF");
    }

    getPanelMute(): boolean | null {
        const ctl = this.control;
        const v = safe(ctl?.getPanelMuteStatus?.bind(ctl));
        return v == null ? null : v === "ON";
    }

    registerRemoteKey(name: string): number | null {
        const input = this.globals.tizen?.tvinputdevice;
        if (!input?.registerKey || !input.getKey) return null;
        try {
            input.registerKey(name);
            return input.getKey(name)?.code ?? null;
        } catch (e) {
            console.warn(`[SignagePanel] could not register remote key ${name}`, e);
            return null;
        }
    }
}
